import {tokens} from '@commercial-helios/ui/theme'
import {m as motion} from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

import type {CookieDetailsFragment} from '@/graphql-types'
import {Chevron} from '@/images/icons/chevron'
import Switch from '@helios/shared/components/switch-ios'
import {Text, text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import {durationStringToNumber} from '@helios/shared/utils/duration-string-to-number'
import mq from '@helios/shared/utils/media-query'

interface Props {
    id: string
    headings: {
        name: string
        origin: string
        duration: string
        purpose: string
    }
    content: CookieDetailsFragment['data']
    toggleState: boolean
    onToggle: () => void
}

const CookieCategory = ({id, headings, content, toggleState, onToggle}: Props) => {
    const [isExpanded, setIsExpanded] = React.useState(false)

    return (
        <Wrapper id={id}>
            <TitleContainer>
                <Text as="h2" variant="title-base">
                    {content.title}
                </Text>

                {content.toggle_text ? (
                    <Text variant="paragraph-base" color="lightMuted">
                        {content.toggle_text}
                    </Text>
                ) : (
                    <SwitchContainer>
                        <Switch
                            checked={toggleState}
                            label={`Toggle ${content.title}`}
                            onChange={onToggle}
                        />
                    </SwitchContainer>
                )}
            </TitleContainer>

            <Description variant="paragraph-large">{content.description}</Description>

            <DisplayCookiesLink onClick={() => setIsExpanded(!isExpanded)}>
                <FocusableText>{content.link_text}</FocusableText>

                <Arrow $isExpanded={isExpanded} />
            </DisplayCookiesLink>

            <motion.div
                initial={isExpanded ? 'active' : 'inactive'}
                animate={isExpanded ? 'active' : 'inactive'}
                variants={variants}
                transition={{
                    type: 'tween',
                    duration: durationStringToNumber(tokens.slow) / 1000,
                }}
            >
                <CookieDetailsContainer>
                    {content.cookies.map((cookie) => (
                        <CookieDetails>
                            <colgroup>
                                <FirstColumn span={1} />

                                <SecondColumn span={1} />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <Text as="td" variant="label-base-medium">
                                        {headings.name}
                                    </Text>

                                    <Text as="td" variant="label-base">
                                        {cookie.name}
                                    </Text>
                                </tr>
                                <tr>
                                    <Text as="td" variant="label-base-medium">
                                        {headings.duration}
                                    </Text>

                                    <Text as="td" variant="label-base">
                                        {cookie.duration}
                                    </Text>
                                </tr>
                                <tr>
                                    <Text as="td" variant="label-base-medium">
                                        {headings.origin}
                                    </Text>

                                    <Text as="td" variant="label-base">
                                        {cookie.origin}
                                    </Text>
                                </tr>
                                <tr>
                                    <Text as="td" variant="label-base-medium">
                                        {headings.purpose}
                                    </Text>

                                    <Text as="td" variant="label-base">
                                        {cookie.purpose}
                                    </Text>
                                </tr>
                            </tbody>
                        </CookieDetails>
                    ))}
                </CookieDetailsContainer>
            </motion.div>
        </Wrapper>
    )
}

const variants = {
    active: {
        opacity: 1,
        height: 'auto',
        overflow: 'hidden',
        display: 'block',
    },
    inactive: {
        opacity: 0,
        height: 0,
        overflow: 'hidden',
        transitionEnd: {
            display: 'none',
        },
    },
}

const mobileMenuBreakpoint = 'sm'

const Wrapper = styled.div`
    padding-bottom: ${tokens.spacing48};

    &:not(:first-of-type) {
        padding-top: ${tokens.spacing40};
    }

    ${mq.min(mobileMenuBreakpoint)} {
        padding-bottom: 80px;

        &:not(:first-of-type) {
            padding-top: 80px;
        }
    }
`

const TitleContainer = styled.div`
    display: flex;
    margin-bottom: ${tokens.spacing28};
    justify-content: space-between;

    ${mq.min(mobileMenuBreakpoint)} {
        align-items: center;
    }
`

const Description = styled(Text)`
    margin-bottom: ${tokens.spacing28};

    ${mq.min(mobileMenuBreakpoint)} {
        margin-bottom: ${tokens.spacing48};
    }
`

const DisplayCookiesLink = styled.div`
    display: grid;
    grid-auto-flow: column;
    width: fit-content;
    grid-column-gap: ${tokens.spacing14};
    align-items: center;
    cursor: pointer;
`

const FocusableText = styled.button`
    all: unset;
    ${text({variant: 'paragraph-base'})}

    :focus {
        ${focusStyles()}
    }
`

const Arrow = styled(Chevron)<{$isExpanded: boolean}>`
    width: ${tokens.spacing12};
    height: ${tokens.spacing12};
    transition: transform ${tokens.smoothInOut};
    transform: ${(props) => props.$isExpanded && 'rotate(-180deg)'};
`

const CookieDetailsContainer = styled.div`
    margin-top: ${tokens.spacing48};
`

const SwitchContainer = styled.div`
    height: ${tokens.spacing20};
`

const CookieDetails = styled.table`
    width: 100%;
    padding: ${tokens.spacing20};
    background-color: ${tokens.shade200};

    & + table {
        margin-top: ${tokens.spacing20};
    }

    tr {
        vertical-align: top;

        ${mq.max(mobileMenuBreakpoint)} {
            display: grid;
        }

        & + tr {
            td {
                padding-top: ${tokens.spacing8};

                ${mq.max(mobileMenuBreakpoint)} {
                    padding-top: 0;
                }
            }

            ${mq.max(mobileMenuBreakpoint)} {
                padding-top: ${tokens.spacing12};
            }
        }
    }
`

const FirstColumn = styled.col`
    width: 15%;

    ${mq.max(mobileMenuBreakpoint)} {
        width: 100%;
    }
`

const SecondColumn = styled.col`
    width: 85%;

    ${mq.max(mobileMenuBreakpoint)} {
        width: 0;
    }
`

export default CookieCategory
